import React, { useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { FaDownload, FaSpinner } from "react-icons/fa";
import { motion } from "framer-motion";
import { CVDocument } from "./CVDocument";
import { useCVData } from "./useCVData";

/**
 * Componente botón para descargar el CV
 * Genera el PDF dinámicamente cuando el usuario hace clic
 *
 * Props:
 * - className: clases CSS personalizadas
 * - children: contenido del botón (por defecto "Descargar CV")
 * - motionProps: props de framer-motion para animaciones
 */
export const DownloadCVButton = ({ className, children, motionProps = {} }) => {
    const { cvData, isLoading, error } = useCVData();
    const [isGenerating, setIsGenerating] = useState(false);

    // Función para generar y descargar el PDF
    const handleDownload = async () => {
        if (!cvData || isGenerating) return;

        try {
            setIsGenerating(true);

            // Generar el PDF
            const blob = await pdf(<CVDocument cvData={cvData} />).toBlob();

            // Crear URL y descargar
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            
            // Generar timestamp: YYYYMMDD_HHMMSS
            const now = new Date();
            const timestamp = now.toISOString()
                .replace(/[-:]/g, '')
                .replace('T', '_')
                .split('.')[0];
            
            const name = cvData.personal?.name?.replace(/\s+/g, "_") || "Portfolio";
            link.download = `CV_${name}_${timestamp}.pdf`;
            link.click();

            // Limpiar
            URL.revokeObjectURL(url);

            // Resetear después de un momento para mostrar feedback visual
            setTimeout(() => setIsGenerating(false), 1000);
        } catch (err) {
            console.error("Error generando CV:", err);
            setIsGenerating(false);
        }
    };

    // Si hay un error o no hay datos, mostrar botón deshabilitado pero visible
    if (error || !cvData) {
        return (
            <motion.button
                disabled
                {...motionProps}
                className={`
          inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3
          font-bold rounded-lg transition-all duration-300 
          opacity-50 cursor-not-allowed
          text-gray-400 border-2 border-gray-400
          ${className || ""}
        `}
            >
                <FaDownload className="w-5 h-5" />
                {children || "Descargar CV"}
            </motion.button>
        );
    }

    // Si está cargando los datos iniciales
    if (isLoading) {
        return (
            <motion.button
                disabled
                {...motionProps}
                className={`
          inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3
          font-bold rounded-lg transition-all duration-300 
          text-green-500 border-2 border-green-500 opacity-70
          ${className || ""}
        `}
            >
                <FaSpinner className="w-5 h-5 animate-spin" />
                Preparando...
            </motion.button>
        );
    }

    // Botón normal con motion - se anima rápidamente
    return (
        <motion.button
            onClick={handleDownload}
            disabled={isGenerating}
            {...motionProps}
            className={`
        inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3
        font-bold rounded-lg transition-all duration-300 hover:text-white
        focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-950
        shadow-lg hover:shadow-xl
        text-green-500 border-2 border-green-500 hover:bg-green-500
        ${isGenerating ? "opacity-75 cursor-wait" : ""}
        ${className || ""}
      `}
        >
            {isGenerating ? (
                <>
                    <FaSpinner className="w-5 h-5 animate-spin" />
                    Generando CV...
                </>
            ) : (
                <>
                    <FaDownload className="w-5 h-5" />
                    {children || "Descargar CV"}
                </>
            )}
        </motion.button>
    );
};
